import { RadioGroup } from "@light-technology/components";
import { Controller, useForm } from "react-hook-form";
import appearanceLogo from "../../assets/appearance-logo.png";
import { api } from "../../state/api";
import {
  BrandMode,
  type AppConfigurationResponse,
  type AppResponse,
} from "../../state/api.types";
import { brandedPlanName } from "../../utilities/planName";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { FormCard } from "../shared/FormCard";
import { BrandModeOption } from "./BrandModeOption";

type AppearanceBrandModeForm = {
  brand_mode: BrandMode;
};

type AppearanceBrandModeCardProps = {
  app: AppResponse;
  configuration: AppConfigurationResponse;
};

export const AppearanceBrandModeCard = (
  props: AppearanceBrandModeCardProps,
) => {
  const [updateConfiguration, updateConfigurationResult] = useMutationWithToast(
    api.useUpdateConfigurationMutation(),
    { successMessage: "The app's brand mode has been updated" },
  );

  const previewTitle = (brandMode: BrandMode) =>
    `Get the ${brandedPlanName(
      { ...props.configuration, brand_mode: brandMode },
      "Buyback",
    )} today`;

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, isValid },
  } = useForm<AppearanceBrandModeForm>({
    defaultValues: { brand_mode: props.configuration.brand_mode },
    mode: "onChange",
  });

  const onSave = handleSubmit(({ brand_mode }) => {
    updateConfiguration({
      appUUID: props.app.uuid,
      company_name: props.configuration.company_name,
      brand_mode,
    })
      .unwrap()
      .then(() => {
        reset({ brand_mode });
      })
      .catch(() => {
        // Error surfaced by useMutationWithToast; keep the form dirty for retry.
      });
  });

  return (
    <FormCard
      name="Brand mode"
      subtext="Determine whether your brand name and logo should be featured in enrollment, plan names, and more. We’ll still use your name as the plan broker in legal disclosures and establish Light as the energy provider."
      buttonText="Save mode"
      isDisabled={!isDirty || !isValid}
      isLoading={updateConfigurationResult.isLoading}
      onButtonClick={onSave}
      data-ph-capture-attribute-action="update-brand-mode"
    >
      <Controller
        control={control}
        name="brand_mode"
        rules={{ required: "This field is required" }}
        render={({ field: { ref: _ref, value, onChange, ...fieldRest } }) => (
          <RadioGroup
            {...fieldRest}
            value={value}
            onChange={onChange}
            aria-label="Brand mode"
            className="flex flex-col gap-5 @page-sm:flex-row"
          >
            <BrandModeOption
              value={BrandMode.Featured}
              label="Featured"
              title={previewTitle(BrandMode.Featured)}
              logoSrc={props.configuration.logo ?? appearanceLogo}
              isSelected={value === BrandMode.Featured}
            />
            <BrandModeOption
              value={BrandMode.Subtle}
              label="Subtle"
              title={previewTitle(BrandMode.Subtle)}
              logoSrc={null}
              isSelected={value === BrandMode.Subtle}
            />
          </RadioGroup>
        )}
      />
    </FormCard>
  );
};
